import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';

const d10  = (v) => (v ? String(v).slice(0, 10) : '—');
const dash = (v) => (v == null || v === '' ? '—' : v);

// [labelKey, getter] — label resolved via t('profile.fields.<labelKey>')
const FIELDS = [
  ['name',       (s) => `${s.first_name} ${s.last_name}`],
  ['employeeId', (s) => dash(s.employee_id)],
  ['email',      (s) => dash(s.email)],
  ['phone',      (s) => dash(s.phone)],
  ['station',    (s) => dash(s.base_airport)],
  ['department', (s) => dash(s.department)],
  ['position',   (s) => dash(s.position)],
  ['category',   (s) => dash(s.professional_category)],
  ['status',     (s) => dash(s.status)],
  ['startDate',  (s) => d10(s.start_date)],
];

// Whitelisted staff-editable fields (mirror of the API's FIELDS registry).
// [key, getter] — label resolved via t('profile.editable.<key>')
const EDITABLE = [
  ['phone',                  (s) => s.phone],
  ['phone_secondary',        (s) => s.phone_secondary],
  ['phone_home',             (s) => s.phone_home],
  ['address',                (s) => s.address],
  ['city',                   (s) => s.city],
  ['postal_code',            (s) => s.postal_code],
  ['marital_status',         (s) => s.marital_status],
  ['emergency_name',         (s) => s.emergency_contact?.name],
  ['emergency_relationship', (s) => s.emergency_contact?.relationship],
  ['emergency_phone',        (s) => s.emergency_contact?.phone],
];

const STATUS_CLS = {
  pending:  'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

export default function MyProfile() {
  const { t, lang } = useI18n();
  const [staff,    setStaff]    = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const [field,   setField]   = useState(EDITABLE[0][0]);
  const [value,   setValue]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const [formMsg, setFormMsg] = useState('');
  const [formErr, setFormErr] = useState('');

  const loadRequests = () => api.get('/change-requests/me').then(({ data }) => setRequests(data.requests)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get('/staff/me').then(({ data }) => setStaff(data.staff)),
      loadRequests(),
    ])
      .catch(() => setError(t('profile.loadError')))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentOf = (key) => {
    const row = EDITABLE.find(([k]) => k === key);
    return staff && row ? (row[1](staff) ?? '') : '';
  };
  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT');

  const submit = async (e) => {
    e.preventDefault();
    setFormMsg(''); setFormErr('');
    if (!value.trim()) { setFormErr(t('profile.errValue')); return; }
    setBusy(true);
    try {
      await api.post('/change-requests', { field, newValue: value.trim() });
      setValue('');
      setFormMsg(t('profile.sent'));
      loadRequests();
    } catch (err) {
      setFormErr(err.response?.data?.message || t('profile.errSend'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />
      <div className="px-5 md:px-0 pb-6 space-y-6">
        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : error ? (
          <div className="card p-6 text-red-600 text-sm">{error}</div>
        ) : !staff ? (
          <div className="card p-8 text-center text-gray-500">{t('common.noRecord')}</div>
        ) : (
          <>
            <div className="card divide-y divide-gray-100">
              {FIELDS.map(([key, get]) => (
                <div key={key} className="flex items-baseline justify-between gap-4 px-5 py-3">
                  <span className="kv-label shrink-0">{t(`profile.fields.${key}`)}</span>
                  <span className="text-sm font-medium text-glk-dark text-right">{get(staff)}</span>
                </div>
              ))}
            </div>

            {/* Request a correction */}
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-glk-dark mb-1">{t('profile.requestTitle')}</h2>
              <p className="text-xs text-gray-500 mb-4">{t('profile.requestLead')}</p>

              {formMsg && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  {formMsg}
                </motion.div>
              )}
              {formErr && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{formErr}</div>
              )}

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label htmlFor="field" className="label">{t('profile.fieldLabel')}</label>
                  <select id="field" className="input" value={field}
                          onChange={(e) => { setField(e.target.value); setFormMsg(''); setFormErr(''); }}>
                    {EDITABLE.map(([k]) => <option key={k} value={k}>{t(`profile.editable.${k}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">{t('profile.currentLabel')}</label>
                  <p className="text-sm text-gray-500 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">{dash(currentOf(field))}</p>
                </div>
                <div>
                  <label htmlFor="value" className="label">{t('profile.newLabel')}</label>
                  <input id="value" className="input" value={value} maxLength={200}
                         onChange={(e) => setValue(e.target.value)} placeholder={t('profile.newPlaceholder')} />
                </div>
                <motion.button type="submit" disabled={busy} whileTap={{ scale: 0.98 }} className="btn-primary w-full">
                  {busy ? t('profile.submitting') : t('profile.submit')}
                </motion.button>
              </form>
            </div>

            {/* My requests */}
            {requests.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-glk-dark">{t('profile.myRequests')}</h2>
                </div>
                <ul className="divide-y divide-gray-100">
                  {requests.map((r) => (
                    <li key={r.id} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-glk-dark">{t(`profile.editable.${r.field}`)}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_CLS[r.status] || STATUS_CLS.pending}`}>{t(`profile.status.${r.status}`)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="line-through text-gray-400">{dash(r.current_value)}</span>
                        {' → '}<span className="text-glk-dark">{r.new_value}</span>
                      </p>
                      {r.review_note && <p className="text-xs text-gray-400 mt-1">{t('profile.hrNote')} {r.review_note}</p>}
                      <p className="text-[11px] text-gray-300 mt-1">{fmtDate(r.created_at)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
